import React from 'react';
import { Alert, Button, Input, Select, Space, Tooltip, Typography } from 'antd';

import { useEditorDoc, useEditorDispatch, useEditorMeta, useEditorUi } from '../../EditorProvider';
import { countGivens, type SudokuDificuldade, type SudokuDoc } from './doc';
import type { SudokuAction } from './reducer';
import type { SudokuUi } from './ui';

// Painel lateral: título, dificuldade + "Gerar" e o placar de casas dadas.
//
// O Select de dificuldade escreve na UI, não no documento: escolher ainda não mudou
// tabuleiro nenhum. Quem grava a escolha no doc é o "Gerar", como metadado da última
// geração (§6) — é dele que o Select parte no próximo load.
//
// "Gerar" chama `generateGrid` (solver.ts), que é síncrono e NÃO é barato: medido em
// 150 execuções em "difícil", p50=31 ms, p75=60 ms, p90=131 ms, p95=168 ms,
// p99=329 ms, máx=366 ms (uma amostra à parte chegou a 787 ms) — ~13% dos cliques
// travam a thread por mais de 100 ms. Em dev o `React.StrictMode` (`src/index.tsx`)
// roda o reducer em dobro, então o gerador também roda duas vezes por clique.
//
// O `setTimeout` no handler existe só para dar UM tick ao navegador pintar o
// `loading` do botão ANTES do cálculo pesado começar: como tudo é síncrono, marcar
// `generating` e disparar o `dispatch` no mesmo turno de evento cairiam no mesmo
// lote de render do React e o spinner nunca chegaria a aparecer na tela.

const DIFICULDADES: Array<{ value: SudokuDificuldade; label: string }> = [
  { value: 'facil', label: 'Fácil (mais casas preenchidas)' },
  { value: 'medio', label: 'Médio' },
  { value: 'dificil', label: 'Difícil (menos casas preenchidas)' },
];

export const Inspector: React.FC = () => {
  const doc = useEditorDoc<SudokuDoc>();
  const { do: dispatchAction } = useEditorDispatch();
  const { editavel } = useEditorMeta();
  const [ui, setUi] = useEditorUi<SudokuUi>();
  const [generating, setGenerating] = React.useState(false);
  const dispatch = (action: SudokuAction) => dispatchAction(action);

  const dadas = countGivens(doc);
  const is9 = doc.shape.size === 9;

  const onGenerate = () => {
    setGenerating(true);
    window.setTimeout(() => {
      dispatch({ type: 'GENERATE', dificuldade: ui.dificuldade });
      setGenerating(false);
    }, 0);
  };

  return (
    <div style={{ minWidth: 300, maxWidth: 380 }}>
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <Input
          addonBefore="Título"
          value={doc.titulo}
          disabled={!editavel}
          onChange={(e) => dispatch({ type: 'SET_TITULO', titulo: e.target.value })}
        />

        <div>
          <Typography.Text strong>Dificuldade da geração</Typography.Text>
          <Select<SudokuDificuldade>
            style={{ width: '100%', marginTop: 4 }}
            value={ui.dificuldade}
            disabled={!editavel || !is9}
            aria-label="Dificuldade da geração"
            onChange={(dificuldade) => setUi((prev) => ({ ...prev, dificuldade }))}
          >
            {DIFICULDADES.map((d) => (
              <Select.Option key={d.value} value={d.value}>
                {d.label}
              </Select.Option>
            ))}
          </Select>
        </div>

        <Tooltip title={is9 ? undefined : 'Sudoku 15×15 só entra por import.'}>
          {/* O <span> é o alvo do tooltip: botão desabilitado não dispara eventos de mouse. */}
          <span style={{ display: 'block' }}>
            <Button
              block
              type="primary"
              ghost
              disabled={!editavel || !is9 || generating}
              loading={generating}
              onClick={onGenerate}
            >
              Gerar tabuleiro
            </Button>
          </span>
        </Tooltip>

        <Typography.Text>
          <strong>{dadas}</strong> de {doc.shape.size ** 2} casas vêm preenchidas.
        </Typography.Text>

        <Alert
          type="info"
          showIcon
          message="Como ajustar"
          description={
            'Clique numa casa para ligar ou desligar o dígito que o jogador já recebe. ' +
            'Quanto menos casas preenchidas, mais difícil — mas a solução precisa continuar ' +
            'única, e o editor avisa quando deixa de ser. Gerar troca o tabuleiro inteiro ' +
            '(dá para desfazer).'
          }
        />
      </Space>
    </div>
  );
};
